// Settings → Activity. A unified event timeline: logins, system errors, domain
// actions, and LLM/TTS cost events — filterable by category.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';

type Category = 'all' | 'auth' | 'error' | 'action' | 'llm';

interface Event {
  id: string;
  ts: number | null;
  category: 'auth' | 'error' | 'action' | 'llm';
  type: string;
  user: string | null;
  summary: string;
  costUsd?: number;
}
interface Resp { events: Event[]; counts: Record<string, number> }

const CAT_STYLE: Record<string, string> = {
  auth: 'text-sky-300 border-sky-300/30',
  error: 'text-red-300 border-red-300/30',
  action: 'text-ink/60 border-ink/20',
  llm: 'text-amber border-amber/40',
};

export function ActivityLogPane() {
  const [cat, setCat] = useState<Category>('all');
  const { data, isLoading } = useQuery<Resp>({
    queryKey: ['admin', 'activity', cat],
    queryFn: () => api<Resp>(`/api/admin/activity?category=${cat}&limit=300`),
    refetchInterval: 20_000,
  });

  return (
    <div>
      <div className="mb-4">
        <h3 className="font-serif text-2xl text-ink m-0">Activity log</h3>
        <p className="font-serif italic text-sm text-ink/55 mt-1 max-w-prose">
          Logins, system errors, key actions, and LLM / voice spend — one timeline.
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-1.5 flex-wrap mb-4">
        {(['all', 'auth', 'error', 'action', 'llm'] as const).map((c) => (
          <button key={c} onClick={() => setCat(c)}
            className={`px-3 py-1.5 rounded-full text-[11.5px] border transition-colors ${ cat === c ? 'bg-ink text-void border-ink' : 'border-ink/15 text-ink/55 hover:text-ink' }`}>
            {c === 'llm' ? 'cost' : c}
            {data?.counts?.[c === 'all' ? 'total' : c] != null && (
              <span className="ml-1.5 opacity-60">{data.counts[c === 'all' ? 'total' : c]}</span>
            )}
          </button>
        ))}
      </div>

      {isLoading || !data ? (
        <div className="text-ink/40 text-[12px]">Loading…</div>
      ) : data.events.length === 0 ? (
        <p className="font-serif italic text-ink/40 text-sm">No events in this category yet.</p>
      ) : (
        <ul className="space-y-1 max-h-[58vh] overflow-y-auto pr-1">
          {data.events.map((e) => (
            <li key={e.id} className="flex items-center gap-3 px-2.5 py-1.5 rounded border border-ink/[0.06] text-[12px]">
              <span className="font-mono text-[9.5px] text-ink/35 w-28 flex-shrink-0">
                {e.ts ? new Date(e.ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
              </span>
              <span className={`px-1.5 py-0.5 rounded-full border text-[10px] flex-shrink-0 ${CAT_STYLE[e.category]}`}>
                {e.category === 'llm' ? 'cost' : e.category}
              </span>
              <span className="font-serif text-ink/80 flex-1 min-w-0 truncate" title={e.summary}>{e.summary}</span>
              {e.user && <span className="font-mono text-[10px] text-ink/40 flex-shrink-0 max-w-[120px] truncate">{e.user}</span>}
              {typeof e.costUsd === 'number' && e.costUsd > 0 && (
                <span className="font-mono text-[11px] text-amber/80 flex-shrink-0">${e.costUsd < 0.01 ? '<0.01' : e.costUsd.toFixed(3)}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
